import React, { useCallback, useContext, useMemo } from 'react';

import { Box } from '@mui/material';
import AccordionCDS from '@sharedCR/coreDesignSystem/AccordionCDS';
import AuditChangesTable from 'app/shared/modules/EntityAuditHistory/components/AuditChangesTable';
import AuditStateMessage from 'app/shared/modules/EntityAuditHistory/components/AuditStateMessage';
import { DETAIL_RETRY_LABEL } from 'app/shared/modules/EntityAuditHistory/EntityAuditHistory.constants';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  COMPARE_COLUMN_LABELS,
  COMPARE_ERROR_STATUS,
  COMPARE_STATE_MESSAGE,
  FORM_HISTORY_AUTOMATIC_FIELDS,
  TemplateRequestStatus,
} from '../../FormChangeHistory.constants';
import { FormTemplateRequestsContext } from '../../FormChangeHistory.context';
import { modalBodyStyles } from '../../FormChangeHistory.styles';
import { FormChangeHistoryTestIds } from '../../FormChangeHistory.testIds';
import {
  getCompareFieldChanges,
  getFieldDifferencesTitle,
  hasCompareDifferences,
  hasTemplateDifferences,
} from '../../FormChangeHistory.utils';
import useTemplateRequest from '../../hooks/useTemplateRequest';
import CompareTemplateDifferences from '../CompareTemplateDifferences';

/**
 * A listed version against the form as it is now, read when the version is picked: the template's
 * differences first, then the other fields, collapsed to one line beside a template. Read-only.
 */
const VersionCompareContent = ({ auditId }) => {
  const requests = useContext(FormTemplateRequestsContext);

  const loadCompare = useCallback(() => requests.getCompare(auditId), [requests, auditId]);

  const { status, data, retry } = useTemplateRequest(loadCompare, 0, COMPARE_ERROR_STATUS);

  const fieldChanges = useMemo(() => getCompareFieldChanges(data?.fields), [data]);

  const isLoaded = status === TemplateRequestStatus.LOADED;
  const hasTemplate = isLoaded && hasTemplateDifferences(data.template);
  const stateMessage =
    isLoaded && hasCompareDifferences(data) ? null : COMPARE_STATE_MESSAGE[status];

  return (
    <Box
      data-status={status}
      data-testid={FormChangeHistoryTestIds.COMPARE_CONTENT}
      sx={modalBodyStyles}
    >
      {!!stateMessage && (
        <AuditStateMessage
          dataTestId={FormChangeHistoryTestIds.COMPARE_STATE}
          isLoading={stateMessage.isLoading}
          message={stateMessage.message}
          retryLabel={DETAIL_RETRY_LABEL}
          retryTestId={FormChangeHistoryTestIds.COMPARE_RETRY_BUTTON}
          onRetry={stateMessage.hasRetry ? retry : null}
        />
      )}

      {hasTemplate && (
        <CompareTemplateDifferences template={data.template} version={data.version} />
      )}

      {!_.isEmpty(fieldChanges) && (
        <Box data-testid={FormChangeHistoryTestIds.COMPARE_FIELDS}>
          <AccordionCDS
            defaultExpanded={!hasTemplate}
            key={auditId}
            title={getFieldDifferencesTitle(fieldChanges.length, hasTemplate)}
            titleVariant="medium14"
          >
            <AuditChangesTable
              automaticFields={FORM_HISTORY_AUTOMATIC_FIELDS}
              changes={fieldChanges}
              columnLabels={COMPARE_COLUMN_LABELS}
              keyPrefix={`compare-${auditId}`}
            />
          </AccordionCDS>
        </Box>
      )}
    </Box>
  );
};

VersionCompareContent.propTypes = {
  auditId: PropTypes.string.isRequired,
};

export default VersionCompareContent;
